<template>
  <ModalVue :modalActive="show" @close="close" maxWidth="max-w-6xl" hasHeaderBorder hasFooterBorder>
    <template #header>
      <div class="w-full">
        <EditableHeader v-model="modelName" placeholder="Nome do modelo" @save="handleSaveHeader" />
      </div>
    </template>

    <div
      class="flex flex-col gap-4 p-4 modal-content w-full items-center overflow-y-auto max-h-[70vh]"
    >
      <div class="flex flex-col md:flex-row gap-4 items-start md:items-center w-full">
        <div
          class="flex flex-col gap-4 bg-[#f0f0f4] p-4 rounded-lg w-full max-w-[95%] md:max-w-[85%] shadow-inner min-w-0"
        >
          <div
            class="flex flex-nowrap overflow-x-auto custom-scrollbar gap-2 pb-2 items-center w-full"
          >
            <div v-for="(col, index) in columns" :key="index" class="relative flex-shrink-0">
              <v-menu
                :model-value="activeColIndex === index"
                :close-on-content-click="false"
                location="bottom start"
                @update:model-value="(open) => handleMenuOpen(open, index, col)"
              >
                <template #activator="{ props: menuProps }">
                  <div
                    v-bind="menuProps"
                    class="flex items-center justify-between px-3 py-2 bg-white border border-gray-400 rounded cursor-pointer min-w-[120px] hover:border-primary transition-colors"
                    :class="{
                      'opacity-80 cursor-not-allowed bg-gray-50 pointer-events-none':
                        isFixedColumn(col)
                    }"
                  >
                    <span
                      class="text-sm font-medium whitespace-nowrap"
                      :class="col ? 'text-gray-700' : 'text-gray-400 italic'"
                    >
                      {{ col || 'Nova coluna' }}
                    </span>
                    <v-icon
                      :class="isFixedColumn(col) ? 'text-gray-400' : 'text-gray-600'"
                      size="20"
                    >
                      {{ mdiMenuDown }}
                    </v-icon>
                  </div>
                </template>
                <v-sheet class="w-[300px] pa-2 rounded-lg">
                  <v-combobox
                    v-model="searchText"
                    :items="columnOptions"
                    @update:model-value="onListItemSelect"
                    @keydown.enter="onConfirmCustomText"
                    placeholder="Selecionar ou digitar..."
                    variant="outlined"
                    density="compact"
                    hide-details
                    autofocus
                    class="mb-1"
                  />
                  <div v-if="activeColIndex !== null && !isFixedColumn(columns[activeColIndex])">
                    <v-divider class="my-2" />
                    <outlined-button
                      text="Excluir coluna"
                      width="100%"
                      height="36px"
                      class="text-red-500 border-red-500"
                      @click="handleRemoveActiveColumn"
                    >
                      <template #prepend>
                        <v-icon size="18" class="mr-[6px] fill-current text-red-500">{{
                          mdiTrashCanOutline
                        }}</v-icon>
                      </template>
                    </outlined-button>
                  </div>
                </v-sheet>
              </v-menu>
            </div>
          </div>
          <div
            class="flex flex-col gap-2 w-full max-h-[280px] overflow-y-auto custom-scrollbar pr-1"
          >
            <div v-for="n in rows" :key="n" class="flex items-center gap-3 w-full flex-shrink-0">
              <div
                class="flex-1 flex items-center justify-center px-2 h-[36px] bg-white border border-gray-400 rounded text-sm font-medium text-gray-700"
              >
                Linha {{ n }}
              </div>
              <div class="border border-red-500 rounded-full bg-white flex-shrink-0">
                <button
                  class="w-[34px] h-[34px] flex items-center justify-center rounded-full text-red-500 hover:bg-red-50 transition-colors"
                  @click="removeRow"
                >
                  <v-icon size="24" class="text-red-500">{{ mdiMinus }}</v-icon>
                </button>
              </div>
            </div>
          </div>
          <div class="flex w-full justify-center pt-1 pb-1 flex-shrink-0">
            <button
              data-testid="add-row-button"
              class="flex w-[132px] items-center justify-center gap-3 h-[36px] px-[16px] rounded bg-white text-[#2F7F91] text-sm font-medium transition-colors hover:bg-gray-50 border border-solid border-[#2f7f91]"
              @click="addRow"
            >
              <v-icon size="16" class="text-[#2F7F91]">{{ mdiPlus }}</v-icon>
              Nova linha
            </button>
          </div>
        </div>
        <button
          data-testid="add-column-button"
          class="flex-shrink-0 flex items-center justify-center gap-1 h-[38px] px-3 rounded bg-white text-[#2F7F91] text-sm font-medium transition-colors hover:bg-gray-50 border border-solid border-[#2f7f91] w-full md:w-auto"
          @click="addColumn"
        >
          <v-icon size="16" class="text-[#2F7F91]">{{ mdiPlus }}</v-icon>
          Nova col.
        </button>
      </div>
    </div>
    <template #footer>
      <div class="flex justify-end gap-4">
        <outlined-button
          text="Fechar"
          width="100px"
          height="42px"
          class="text-primary border-primary"
          @click="close"
        />
        <filled-button text="Salvar" width="100px" height="42px" color="primary" @click="save" />
      </div>
    </template>
  </ModalVue>
</template>

<script setup lang="ts">
import { ref, watch, computed, PropType } from 'vue'
import ModalVue from '@/components/designSystem/modals/Modal.vue'
import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import EditableHeader from '@/components/designSystem/Inputs/EditableHeader.vue'
import { mdiMenuDown, mdiMinus, mdiPlus, mdiTrashCanOutline } from '@mdi/js'
import { DSAttendanceMonitoringType } from '@/constants/ds-attendance.constants'

// Tipagens alinhadas com o Payload do Backend
interface SheetTemplateFields {
  columns: string[]
  rows: number
}

interface SheetData {
  id?: number
  name: string
  templateFields: SheetTemplateFields
}

const props = defineProps({
  show: { type: Boolean, required: true },
  initialData: { type: Object as PropType<SheetData | null>, default: null },
  monitoringType: {
    type: String as PropType<DSAttendanceMonitoringType>,
    default: DSAttendanceMonitoringType.FOLHA
  }
})

const emit = defineEmits(['close', 'save'])

const modelName = ref('Novo Modelo de Folha')
const columns = ref<string[]>([])
const rows = ref<number>(0)

const activeColIndex = ref<number | null>(null)
const searchText = ref('')

const FIXED_COLUMNS = computed(() => {
  const base = ['Nome do médico', 'CRM', 'Data', 'Jornada início', 'Jornada Fim', 'Entrada']
  if (props.monitoringType !== DSAttendanceMonitoringType.EXTERNAL_APP) {
    base.push('Ass. e Carimbo Entrada')
  }
  base.push('Saída')
  if (props.monitoringType !== DSAttendanceMonitoringType.EXTERNAL_APP) {
    base.push('Ass. e Carimbo Saída')
  }
  return base
})
const columnOptions = ref(['Setor', 'Especialidade', 'Valor do plantão'])

const isFixedColumn = (columnName: string) => FIXED_COLUMNS.value.includes(columnName)

// Controlo do Modal
watch(
  () => props.show,
  (isShowing) => {
    if (isShowing) {
      if (props.initialData?.templateFields) {
        templateId.value = props.initialData.id
        modelName.value = props.initialData.name
        columns.value = [...props.initialData.templateFields.columns]
        rows.value = props.initialData.templateFields.rows
      } else {
        templateId.value = undefined
        modelName.value = 'Novo Modelo de Folha'
        columns.value = [...FIXED_COLUMNS.value]
        rows.value = 3
      }
    }
  }
)

const close = () => {
  activeColIndex.value = null
  emit('close')
}

// O Payload aqui já está aderente ao Backend (templateFields com JSONB)
const templateId = ref<number | undefined>(undefined)

const save = () => {
  emit('save', {
    id: templateId.value,
    name: modelName.value,
    templateFields: {
      columns: columns.value,
      rows: rows.value
    }
  })
}

const handleSaveHeader = (newName: string) => {
  modelName.value = newName
}

// Lógica de Linhas e Colunas
const addColumn = () => columns.value.push('')
const removeRow = () => {
  if (rows.value > 0) rows.value--
}
const addRow = () => rows.value++

const handleMenuOpen = (open: boolean, index: number, col: string) => {
  if (open && !isFixedColumn(col)) {
    activeColIndex.value = index
    searchText.value = col
  } else if (!open && activeColIndex.value !== null) {
    const trimmed = typeof searchText.value === 'string' ? searchText.value.trim() : ''
    if (trimmed) {
      columns.value[activeColIndex.value] = trimmed
      if (!columnOptions.value.includes(trimmed)) {
        columnOptions.value.push(trimmed)
      }
    }
    activeColIndex.value = null
  }
}

const onListItemSelect = (value: string) => {
  if (activeColIndex.value === null || !value) return
  if (!columnOptions.value.includes(value)) return

  columns.value[activeColIndex.value] = value
  activeColIndex.value = null
}

const onConfirmCustomText = () => {
  if (activeColIndex.value === null) return
  const trimmed = typeof searchText.value === 'string' ? searchText.value.trim() : ''
  if (!trimmed) return

  columns.value[activeColIndex.value] = trimmed
  if (!columnOptions.value.includes(trimmed)) {
    columnOptions.value.push(trimmed)
  }
  activeColIndex.value = null
}

const handleRemoveActiveColumn = () => {
  if (activeColIndex.value !== null) {
    columns.value.splice(activeColIndex.value, 1)
    activeColIndex.value = null
  }
}
</script>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: #e5e7eb;
  border-radius: 4px;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #9ca3af;
  border-radius: 4px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}

@media (max-height: 500px) {
  .modal-content {
    max-height: 70vh;
    overflow-y: auto;
  }
}
</style>
